import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { List } from '@mic3/platform-ui';
import Icon from 'app/components/atoms/Icon/Icon';
import {
    SectionTitle,
    StyledListItem,
    StyledListItemIcon,
    StyledListItemText,
    NoResults,
    SearchIcon,
} from '../GlobalSearchModal.styles';

const FilterList = ({ filters, searchQuery, onFilterClick, onFreeTextClick, allFiltersUsed, highlightedIndex }) => {
    const highlightedRef = useRef(null);
    const hasFreeTextOption = searchQuery?.trim() && onFreeTextClick;
    
    // Auto-scroll to highlighted item
    useEffect(() => {
        if (highlightedRef.current) {
            highlightedRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [highlightedIndex]);
    
    if (filters.length === 0 && !hasFreeTextOption) {
        return (
            <>
                <SectionTitle variant="caption">Filter by</SectionTitle>
                <NoResults>
                    <SearchIcon name="filter-off-outline" size="lg" />
                    {allFiltersUsed ? (
                        <>
                            <p>All filters have been used</p>
                            <small>Press Enter to execute search</small>
                        </>
                    ) : (
                        <>
                            <p>No matching filters</p>
                            <small>Press Enter to search for "{searchQuery}"</small>
                        </>
                    )}
                </NoResults>
            </>
        );
    }
    
    // Calculate actual index for filters (offset by 1 if free text option exists)
    const getFilterHighlightIndex = (filterIndex) => {
        return hasFreeTextOption ? filterIndex + 1 : filterIndex;
    };
    
    return (
        <>
            {/* Search for text option - shown when user has typed something */}
            {hasFreeTextOption && (
                <>
                    <SectionTitle variant="caption">Search</SectionTitle>
                    <List>
                        <StyledListItem
                            ref={highlightedIndex === 0 ? highlightedRef : null}
                            button
                            onClick={() => onFreeTextClick(searchQuery.trim())}
                            $highlighted={highlightedIndex === 0}
                        >
                            <StyledListItemIcon>
                                <Icon name="format-text" size="sm" />
                            </StyledListItemIcon>
                            <StyledListItemText primary={`Search for "${searchQuery.trim()}"`} />
                        </StyledListItem>
                    </List>
                </>
            )}
            
            {/* Filter options */}
            {filters.length > 0 && (
                <>
                    <SectionTitle variant="caption">Filter by</SectionTitle>
                    <List>
                        {filters.map((filter, index) => {
                            const isHighlighted = highlightedIndex === getFilterHighlightIndex(index);
                            return (
                                <StyledListItem
                                    key={filter.key || index}
                                    ref={isHighlighted ? highlightedRef : null}
                                    button
                                    onClick={() => onFilterClick(filter)}
                                    $highlighted={isHighlighted}
                                >
                                    <StyledListItemIcon>
                                        <Icon name={filter.icon} size="sm" />
                                    </StyledListItemIcon>
                                    <StyledListItemText primary={filter.text} secondary={filter.subtitle} />
                                </StyledListItem>
                            );
                        })}
                    </List>
                </>
            )}
        </>
    );
}

FilterList.propTypes = {
    filters: PropTypes.array.isRequired,
    searchQuery: PropTypes.string,
    onFilterClick: PropTypes.func.isRequired,
    onFreeTextClick: PropTypes.func,
    allFiltersUsed: PropTypes.bool,
    highlightedIndex: PropTypes.number,
};

FilterList.defaultProps = {
    searchQuery: '',
    allFiltersUsed: false,
    onFreeTextClick: null,
    highlightedIndex: -1,
};

export default FilterList;

